import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  createServices,
  getServicesByID,
  ServicesDetailsImageUpload,
  updateServices,
} from "./services/services.services";
import { clearServiceDetails } from "./servicesSlice";
import { getAllActiveCoreServices } from "../coreservices/services/coreservices.services";
import { slugify } from "../../_helper/helper";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateServices: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ServicesID = location.state?.ID;
  const ServicesDetail = useAppSelector(
    (state) => state.services.ServiceDetail,
  );
  const loading = useAppSelector((state) => state.services.loading);
  const [formKey, setFormKey] = React.useState(1);
  const CoreServiceList = useAppSelector(
    (state) => state.coreservices.CoreServiceList,
  );

  useEffect(() => {
    if (ServicesID) {
      dispatch(getServicesByID(ServicesID));
    }
    dispatch(getAllActiveCoreServices());
    return () => {
      dispatch(clearServiceDetails());
    };
  }, [ServicesID]);

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ServicesDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      CoreServiceID: values.CoreServiceID ?? null,
      Title: values.Title ?? "",
      SubTitle: values.SubTitle ?? "",
      PageDetails: values.PageDetails ?? "",
      CtaTemplate: values.CtaTemplate ?? "",
      CtaImage: values.CtaImage ?? null,
      BreakDown: values.BreakDown ?? "",
      BreakDownImage: values.BreakDownImage ?? null,
      URL: values.URL ?? "",
      MetaTitle: values.MetaTitle ? values.MetaTitle : "",
      MetaKeywords: values.MetaKeywords ? values.MetaKeywords : "",
      MetaDescription: values.MetaDescription ? values.MetaDescription : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const CtaImage =
      values?.CtaImage &&
      values?.CtaImage.length &&
      typeof values?.CtaImage !== "string"
        ? values.CtaImage[0].getRawFile()
        : null;
    const BreakDownImage =
      values?.BreakDownImage &&
      values?.BreakDownImage.length &&
      typeof values?.BreakDownImage !== "string"
        ? values.BreakDownImage[0].getRawFile()
        : null;
    try {
      if (ServicesID) {
        const editPayload = {
          ...formData,
          ID: ServicesID,
        };

        const response = await dispatch(updateServices(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.CtaImage && typeof values?.CtaImage !== "string") ||
            (values?.BreakDownImage &&
              typeof values?.BreakDownImage !== "string")
          ) {
            ImageData.append("CtaImage", CtaImage);
            ImageData.append("BreakDownImage", BreakDownImage);
            ImageData.append("ID", ServicesID);
            const imageResponse = await dispatch(
              ServicesDetailsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/services");
            } else {
              navigate("/services");
            }
          } else {
            navigate("/services");
          }
        }
      } else {
        const response = await dispatch(createServices(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.CtaImage && typeof values?.CtaImage !== "string") ||
            (values?.BreakDownImage &&
              typeof values?.BreakDownImage !== "string" &&
              response?.payload?.Data?.ID)
          ) {
            ImageData.append("CtaImage", CtaImage);
            ImageData.append("BreakDownImage", BreakDownImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              ServicesDetailsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/services");
            } else {
              navigate("/services");
            }
          } else {
            navigate("/services");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <div>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={ServicesDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ServicesID ? "Update  Service" : "Create  Service"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayout>
                    <GridLayoutItem colSpan={1}>
                      <Field
                        name="Title"
                        label="Title"
                        placeholder="Title"
                        onChange={(e) => handleCreateSlug(e, formRenderProps)}
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={1}>
                      <Field
                        name="URL"
                        label="URL"
                        placeholder="URL"
                        astrike={true}
                        component={FormTextField}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`CoreServiceID`}
                        label="Core Service"
                        placeholder="Core Service ID"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={CoreServiceList?.map((coreservices: any) => {
                          return {
                            value: coreservices?.ID,
                            label: coreservices?.Title,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`IsActive`}
                        label="Status"
                        placeholder="i.e. Active"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={STATUSARRAY?.map((status: any) => {
                          return {
                            value: status?.value,
                            label: status?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="PageDetails"
                      label="Service Detail"
                      placeholder="Service Detail"
                      component={FormRichTextField}
                      minHeight={100}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`CtaImage`}
                      label="CTA Image"
                      type="file"
                      accept="image/*"
                      alternateName="CTA Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="CtaTemplate"
                      label="CTA Detail"
                      placeholder="CTA Detail"
                      component={FormRichTextField}
                      minHeight={100}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`BreakDownImage`}
                      label="Breakdown Image"
                      type="file"
                      accept="image/*"
                      alternateName="Breakdown Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="BreakDown"
                      label="BreakDown"
                      placeholder="BreakDown"
                      component={FormRichTextField}
                      minHeight={100}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title "
                      placeholder="Meta Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Key Words"
                      placeholder="Meta Key words"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3} style={{ width: "100%" }}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      placeholder="Meta Description"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ServicesID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/services")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateServices;
